import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import {
  createIsIssueFieldVisible,
  PDF_ESTIMATE_SECTIONS,
  PDF_SHARED_SECTION_ORDER,
  PdfEstimateSectionKey,
} from '../../pdfFieldSections';
import { ReportIssueEstimateRow } from '../../reportIssueEstimateRow';
import { InspectionPageEstimateInput } from '../buildInspectionPageEstimateInput';
import {
  buildInspectionChecklistFlatNodes,
  formatChecklistIndexPath,
  ProtocolPdfNodeType,
} from '../inspectionChecklistTree';
import { pdfFieldTable, PdfField, PdfFieldTableEntry } from './inspectionFieldTable';
import {
  compactAttachmentsBlock,
  documentCountsToPdfAttachments,
} from './inspectionPdfAttachments';
import { INSPECTION_PDF_LABELS, INSPECTION_PDF_SAMPLE_VALUES } from './inspectionPdfLabels';
import { sectionHeading } from './inspectionPdfTypography';
import { pdfColumn, PdfContent, pdfSinglePageColumn, PdfTableCell, pdfText } from './pdf/pdfBlocks';
import { boldFont } from './pdf/pdfTextMetrics';

const ENTRY_PAIRS_PER_ROW = 2;
const ENTRY_CAPTION_SHARE = 0.52;
const ENTRY_MARGIN = { top: 12 };
const ENTRY_HEADER_FONT = boldFont(11);
/** The attachments row is merged across the entry table: one cell padding on each side. */
const ATTACHMENTS_CELL_GEOMETRY = { widthRatio: 1, horizontalPadding: 8 };

/** Key information fields printed between the title and the description (site and level come first). */
const KEY_INFO_FIELDS: IssueFieldNames[] = PDF_ESTIMATE_SECTIONS[PdfEstimateSectionKey.baseInfo].filter(
  (fieldName) =>
    ![
      IssueFieldNames.title,
      IssueFieldNames.site,
      IssueFieldNames.level,
      IssueFieldNames.positionOnMap,
      IssueFieldNames.description,
      IssueFieldNames.hashtag,
    ].includes(fieldName)
);

/** Labelled fields of the sections the issue report prints under their own headings, in their order. */
const SHARED_SECTION_FIELDS: IssueFieldNames[] = PDF_SHARED_SECTION_ORDER.flatMap(
  (sectionKey) => PDF_ESTIMATE_SECTIONS[sectionKey]
);

/** A field whose value the estimate does not hold: as long as a typical dictionary label. */
const sampleField = (): PdfField => ({
  label: INSPECTION_PDF_SAMPLE_VALUES.shortFieldValue,
  value: INSPECTION_PDF_SAMPLE_VALUES.shortFieldValue,
});

/**
 * `PdfInspectionIssueRegisterFactory`: one bordered table per issue — a header row with the register
 * position, then its form fields two pairs per row and its attachments merged across the table.
 */
class IssueRegisterBuilder {
  private readonly relatedItemLabels: Map<string, string>;

  constructor(private readonly input: InspectionPageEstimateInput) {
    this.relatedItemLabels = new Map(
      buildInspectionChecklistFlatNodes(input.source.inspection).flatMap((node) =>
        node.type === ProtocolPdfNodeType.item
          ? [
              [
                node.protocolItem._id,
                [formatChecklistIndexPath(node.indexPath), node.checklistItem.summary]
                  .filter(Boolean)
                  .join(' - '),
              ],
            ]
          : []
      )
    );
  }

  entries(): PdfContent[] {
    return this.input.source.issueRows.map((row, index) => this.entry(row, index + 1));
  }

  private entry(row: ReportIssueEstimateRow, position: number): PdfContent {
    const header = `${INSPECTION_PDF_LABELS.registerPosition} ${position} · ${INSPECTION_PDF_LABELS.issueShort} #${INSPECTION_PDF_SAMPLE_VALUES.issueCode}`;
    const headerRow: PdfTableCell[] = [
      {
        colSpan: ENTRY_PAIRS_PER_ROW * 2,
        content: (width) => [pdfText(header, width, { font: ENTRY_HEADER_FONT })],
      },
    ];
    return pdfFieldTable({
      entries: [...this.fields(row), this.attachments(row)],
      pairsPerRow: ENTRY_PAIRS_PER_ROW,
      captionShare: ENTRY_CAPTION_SHARE,
      leadingRows: [headerRow],
      margin: ENTRY_MARGIN,
    });
  }

  /** `entryFields`: the related element, stage, title, location, key information, description, the rest. */
  private fields({ issue }: ReportIssueEstimateRow): PdfField[] {
    const isVisible = createIsIssueFieldVisible(issue, this.input);
    const relatedItem = issue.protocolItem
      ? this.relatedItemLabels.get(issue.protocolItem)
      : undefined;
    const visibleSamples = (fieldNames: IssueFieldNames[]): PdfField[] =>
      fieldNames.filter(isVisible).map(sampleField);
    const optional = (fieldName: IssueFieldNames, value: PdfField): PdfField[] =>
      isVisible(fieldName) ? [value] : [];

    return [
      ...(relatedItem ? [{ label: INSPECTION_PDF_LABELS.relatedChecklistItem, value: relatedItem }] : []),
      ...optional(IssueFieldNames.stage, {
        label: INSPECTION_PDF_LABELS.stage,
        value: INSPECTION_PDF_SAMPLE_VALUES.issueStage,
      }),
      ...optional(IssueFieldNames.title, {
        label: INSPECTION_PDF_LABELS.issueTitle,
        value: issue.primaryData.title ?? '',
      }),
      { label: INSPECTION_PDF_LABELS.site, value: INSPECTION_PDF_SAMPLE_VALUES.shortFieldValue },
      { label: INSPECTION_PDF_LABELS.level, value: INSPECTION_PDF_SAMPLE_VALUES.shortFieldValue },
      ...visibleSamples(KEY_INFO_FIELDS),
      ...optional(IssueFieldNames.description, {
        label: INSPECTION_PDF_LABELS.issueDescription,
        value: issue.primaryData.description ?? '',
      }),
      ...visibleSamples(SHARED_SECTION_FIELDS),
    ];
  }

  private attachments(row: ReportIssueEstimateRow): PdfFieldTableEntry {
    return {
      fullRow: (contentWidth) =>
        compactAttachmentsBlock(
          {
            attachments: documentCountsToPdfAttachments(row.primaryDocuments),
            config: this.input.issueAttachments,
            fileMetadata: this.input.fileMetadata,
            label: INSPECTION_PDF_LABELS.issueAttachments,
            geometry: ATTACHMENTS_CELL_GEOMETRY,
          },
          contentWidth
        ),
    };
  }
}

export function inspectionIssueRegisterSection(
  input: InspectionPageEstimateInput,
  sectionNumber?: number
): PdfContent {
  return pdfColumn([
    pdfSinglePageColumn([sectionHeading(INSPECTION_PDF_LABELS.issueRegisterHeading, sectionNumber)]),
    ...new IssueRegisterBuilder(input).entries(),
  ]);
}
